<template>
  <TransitionRoot as="template" :show="show">
    <Dialog as="div" class="relative z-50" @close="handleClose">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6"
            >
              <div>
                <!-- Header -->
                <div class="flex items-center justify-between mb-6">
                  <div class="flex items-center gap-3">
                    <div class="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                      <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                      </svg>
                    </div>
                    <DialogTitle as="h3" class="text-2xl font-semibold leading-6 text-gray-900">
                      What's New in v{{ displayVersion }}
                    </DialogTitle>
                  </div>
                  <button @click="handleClose" class="text-gray-400 hover:text-gray-500">
                    <XMarkIcon class="h-6 w-6" />
                  </button>
                </div>

                <!-- Loading state -->
                <div v-if="isLoading" class="flex items-center justify-center py-12">
                  <svg class="animate-spin h-10 w-10 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>

                <!-- Changelog Content -->
                <div v-else class="changelog-content prose prose-sm max-w-none max-h-[60vh] overflow-y-auto" v-html="renderedMarkdown"></div>
              </div>

              <!-- Actions -->
              <div class="mt-6 flex justify-end">
                <button
                  type="button"
                  @click="handleAcknowledge"
                  class="inline-flex justify-center rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                >
                  Got it, thanks!
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { marked } from 'marked';

interface Props {
  show: boolean;
  version: string;
  markdownContent: string;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false
});

const emit = defineEmits(['close', 'acknowledge']);

// Configure marked for better rendering
marked.setOptions({
  breaks: true,
  gfm: true
});

// Clean version for display (remove "dev-" prefix)
const displayVersion = computed(() => {
  return props.version.replace(/^dev-/, '');
});

const renderedMarkdown = computed(() => {
  if (!props.markdownContent) return '';
  return marked(props.markdownContent);
});

function handleClose() {
  emit('close');
}

function handleAcknowledge() {
  emit('acknowledge');
}
</script>

<style scoped>
/* Changelog-specific styling */
:deep(.changelog-content) {
  @apply text-gray-700;
}

:deep(.changelog-content h1) {
  @apply text-2xl font-bold text-gray-900 mb-4 mt-6 first:mt-0;
}

:deep(.changelog-content h2) {
  @apply text-xl font-semibold text-gray-900 mb-3 mt-6 pb-2 border-b border-gray-200;
}

:deep(.changelog-content h3) {
  @apply text-lg font-semibold text-gray-800 mb-2 mt-4;
}

:deep(.changelog-content p) {
  @apply mb-3;
}

:deep(.changelog-content ul) {
  @apply list-disc list-inside mb-4 space-y-2;
}

:deep(.changelog-content li) {
  @apply text-gray-700;
}

:deep(.changelog-content strong) {
  @apply font-semibold text-gray-900;
}

:deep(.changelog-content hr) {
  @apply my-6 border-gray-300;
}

:deep(.changelog-content code) {
  @apply bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-red-600;
}

/* Scrollbar styling */
.changelog-content::-webkit-scrollbar {
  width: 8px;
}

.changelog-content::-webkit-scrollbar-track {
  @apply bg-gray-100 rounded;
}

.changelog-content::-webkit-scrollbar-thumb {
  @apply bg-gray-400 rounded hover:bg-gray-500;
}
</style>
